import React from "react";
import { LegalPage, LegalSection, LegalList } from "../components/SiteShell";
import { usePageMeta } from "../lib/usePageMeta";

export default function Terms() {
  usePageMeta({
    title: "Terms & Conditions",
    description: "The terms of service governing your use of Fitnudge - a software platform for gyms and fitness businesses.",
  });

  return (
    <LegalPage
      testid="terms-page"
      title="Terms & Conditions"
      subtitle="By accessing or using Fitnudge, you agree to these Terms."
      lastUpdated="30 May 2026"
    >
      <LegalSection title="Service Description">
        <p>Fitnudge provides software tools for:</p>
        <LegalList items={[
          "Lead management",
          "Member management",
          "Trial tracking",
          "Attendance tracking",
          "Payment tracking",
          "Communication workflows",
          "Automation tools",
          "AI-powered operational insights",
        ]}/>
      </LegalSection>

      <LegalSection title="User Responsibilities">
        <p>You agree to use Fitnudge only for lawful purposes related to running your fitness business and not to:</p>
        <LegalList items={[
          "Misuse the platform or attempt to access it through unauthorized means",
          "Upload content you do not have the right to store or share",
          "Use Fitnudge to contact members who have opted out of receiving messages",
          "Reverse engineer, scrape, or resell the service",
        ]}/>
      </LegalSection>

      <LegalSection title="Account Security">
        <p>Users are responsible for:</p>
        <LegalList items={[
          "Maintaining account security",
          "Protecting login credentials",
          "Accuracy of information entered into the platform",
        ]}/>
      </LegalSection>

      <LegalSection title="Data Responsibility">
        <p>
          Users are responsible for ensuring they have lawful rights and
          permissions to store and process any data entered into Fitnudge -
          including member contact details, attendance, payment records, and
          fitness information.
        </p>
      </LegalSection>

      <LegalSection title="Subscription Terms">
        <p>
          Access to certain features requires a paid subscription. Subscription
          fees are charged in advance and may be modified with prior notice.
          Pricing and plan details are displayed on the in-app billing page.
        </p>
      </LegalSection>

      <LegalSection title="WhatsApp Integration Disclaimer">
        <p>
          Fitnudge relies on WhatsApp deep links and (where enabled) the
          WhatsApp Business Platform operated by Meta. We are not responsible
          for WhatsApp outages, Meta policy changes, message delivery failures,
          or third-party service interruptions.
        </p>
      </LegalSection>

      <LegalSection title="AI Feature Disclaimer">
        <p>
          AI-generated insights, recommendations, workout plans, nutrition
          plans, and operational suggestions are provided for informational
          purposes only. They may be incomplete, inaccurate, or unsuitable for
          your specific business. Users should exercise independent judgment
          before acting on AI-generated outputs.
        </p>
      </LegalSection>

      <LegalSection title="Third-Party Services Disclaimer">
        <p>
          Fitnudge integrates with third-party services (Razorpay, Google,
          Supabase, Jotform, analytics providers and others). We are not
          responsible for the availability, accuracy, or actions of these
          third-party services.
        </p>
      </LegalSection>

      <LegalSection title="Suspension & Termination">
        <p>We may suspend or terminate accounts that:</p>
        <LegalList items={[
          "Violate these Terms",
          "Engage in unlawful activity",
          "Abuse the platform or other users",
          "Fail to pay subscription fees when due",
        ]}/>
      </LegalSection>

      <LegalSection title="No Guarantee of Business Results">
        <p>
          Fitnudge does <strong>not</strong> guarantee any specific business
          results, lead conversions, member retention rates, or revenue
          outcomes. Results depend on how you use the tools, your business
          context, and many factors outside our control.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of Liability">
        <p>To the maximum extent permitted by law, Fitnudge shall not be liable for:</p>
        <LegalList items={[
          "Lost revenue",
          "Lost profits",
          "Business interruptions",
          "Data loss",
          "Indirect, incidental, or consequential damages",
        ]}/>
      </LegalSection>

      <LegalSection title="Governing Law">
        <p>
          These Terms shall be governed by and construed in accordance with the
          laws of India. Any disputes will be subject to the exclusive
          jurisdiction of the competent courts in India.
        </p>
      </LegalSection>

      <LegalSection title="Changes to These Terms">
        <p>
          We may update these Terms from time to time. We will notify users of
          material changes by email or via the platform. Continued use of
          Fitnudge after such changes constitutes acceptance of the new Terms.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          <a href="mailto:support@fitnudge.in" className="text-primary hover:underline">support@fitnudge.in</a>
        </p>
      </LegalSection>
    </LegalPage>
  );
}
